import { useEffect, useState } from "react";
import { auth, db } from "./firebase";
import { onAuthStateChanged, signOut } from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";
import AuthPage from "./AuthPage";
import PollPage from "./PollPage";
import AdminPage from "./AdminPage";
import StatsPage from "./StatsPage";
import ProfilePage from "./ProfilePage";
import SessionsInfoPage from "./SessionsInfoPage";
import ContactPage from "./ContactPage";
import HomePage from "./HomePage";
import LiveSessionPage from "./LiveSessionPage";
import { pageWrap, colors, btnStyle } from "./lib/theme";
import logo from "./assets/logo.png";

function TabNav({ tabs, active, onSelect }) {
  return (
    <div style={{ display: "flex", gap: 8, marginBottom: 20, borderBottom: "1px solid " + colors.border, overflowX: "auto" }}>
      {tabs.map(function (t) {
        var isActive = active === t.id;
        return (
          <button
            key={t.id}
            onClick={function () { onSelect(t.id); }}
            style={{
              background: "none", border: "none", cursor: "pointer",
              padding: "10px 4px", marginRight: 16, fontSize: 14, whiteSpace: "nowrap",
              fontWeight: isActive ? 700 : 500,
              color: isActive ? colors.teal : colors.textDim,
              borderBottom: isActive ? "2px solid " + colors.teal : "2px solid transparent",
            }}
          >
            {t.label}
          </button>
        );
      })}
    </div>
  );
}

function Brand() {
  return (
    <div style={{ display: "flex", alignItems: "center" }}>
      <img src={logo} alt="East Anglia Pickleball" style={{ height: 28 }} />
    </div>
  );
}

export default function App() {
  var [user, setUser] = useState(undefined); // undefined = loading, null = logged out
  var [tab, setTab] = useState("poll");
  var [activeSession, setActiveSession] = useState(null); // { template, date }
  var [showAuth, setShowAuth] = useState(false);
  var [publicTab, setPublicTab] = useState("home");
  var [deactivatedMessage, setDeactivatedMessage] = useState(false);

  useEffect(function () {
    var unsub = onAuthStateChanged(auth, function (fbUser) {
      if (!fbUser) { setUser(null); return; }
      getDoc(doc(db, "users", fbUser.uid)).then(function (snap) {
        var data = snap.exists() ? snap.data() : {};
        if (data.active === false) {
          setDeactivatedMessage(true);
          signOut(auth);
          setUser(null);
          return;
        }
        setUser({ uid: fbUser.uid, email: fbUser.email, displayName: data.displayName || fbUser.email, isAdmin: !!data.isAdmin });
      });
    });
    return unsub;
  }, []);

  if (user === undefined) {
    return <div style={{ ...pageWrap, textAlign: "center", padding: "80px 16px 60px" }}>Loading…</div>;
  }

  if (!user) {
    if (showAuth) {
      return (
        <div style={pageWrap}>
          <button
            onClick={function () { setShowAuth(false); }}
            style={{ background: "none", border: "none", color: colors.teal, cursor: "pointer", fontSize: 13, padding: 0, marginBottom: 6 }}
          >
            {"← Back"}
          </button>
          <AuthPage onLogin={setUser} />
        </div>
      );
    }
    var publicTabs = [
      { id: "home", label: "Home" },
      { id: "sessions", label: "Sessions" },
      { id: "contact", label: "Contact" },
    ];
    return (
      <div style={pageWrap}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
          <Brand />
          <button onClick={function () { setShowAuth(true); }} style={{ ...btnStyle, width: "auto", padding: "8px 16px", fontSize: 13 }}>
            Log In
          </button>
        </div>

        {deactivatedMessage && (
          <div style={{ background: colors.dangerBg, border: "1px solid " + colors.dangerBorder, borderRadius: 8, padding: "10px 14px", fontSize: 13, color: colors.danger, marginBottom: 16 }}>
            Your account has been deactivated by an admin. Get in touch if you think this is a mistake.
          </div>
        )}

        <TabNav tabs={publicTabs} active={publicTab} onSelect={setPublicTab} />

        {publicTab === "home" && (
          <HomePage
            onJoin={function () { setPublicTab("sessions"); }}
            onLogIn={function () { setShowAuth(true); }}
            onContact={function () { setPublicTab("contact"); }}
          />
        )}
        {publicTab === "sessions" && <SessionsInfoPage />}
        {publicTab === "contact" && <ContactPage user={null} />}
      </div>
    );
  }

  if (activeSession) {
    return (
      <div style={pageWrap}>
        <LiveSessionPage
          user={user}
          template={activeSession.template}
          occurrenceDate={activeSession.date}
          onExit={function () { setActiveSession(null); }}
        />
      </div>
    );
  }

  var tabs = [
    { id: "poll", label: "Poll" },
    { id: "sessions", label: "Sessions" },
    { id: "contact", label: "Contact" },
    { id: "stats", label: "Stats" },
    { id: "profile", label: "Profile" },
  ];
  if (user.isAdmin) tabs.push({ id: "admin", label: "Admin" });

  return (
    <div style={pageWrap}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
        <Brand />
        <button
          onClick={function () { signOut(auth); }}
          style={{ background: "none", border: "none", color: colors.textDim, cursor: "pointer", fontSize: 12 }}
        >
          {user.displayName} · Log out
        </button>
      </div>

      <TabNav tabs={tabs} active={tab} onSelect={setTab} />

      {tab === "poll" && (
        <PollPage
          user={user}
          onOrganise={function (template, date) { setActiveSession({ template: template, date: date }); }}
        />
      )}
      {tab === "sessions" && <SessionsInfoPage />}
      {tab === "contact" && <ContactPage user={user} />}
      {tab === "stats" && <StatsPage />}
      {tab === "profile" && <ProfilePage user={user} />}
      {tab === "admin" && user.isAdmin && <AdminPage user={user} />}
    </div>
  );
}
